









































































































































import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

export default {
    name: 'UnlistedJobInviteForm',

    props: {
        applicant_uuid: {
            required: true,
            type: String,
        },

        applicant_name: {
            required: true,
            type: String,
        },
    },

    setup(props) {
        const numeric = helpers.regex(/^[0-9]*$/);

        let error = ref<string | null>(null);
        let loading = ref<boolean>(false);
        const salaryTypes = reactive(['Annual', 'Monthly', 'Hourly', 'Commission']);

        const initialFormData = () => ({
            candidate_uuid: props.applicant_uuid,
            job_title: null,
            description: null,
            document: null,
            note: null,
            salary_type: null,
            min: 0,
            max: 0,
            hours_per_month: null,
            basic_min: 0,
            basic_max: 0,
            comm_min: 0,
            comm_max: 0,
        });

        const form = reactive<object>(initialFormData());

        const hasSalaryType = computed(() => form.salary_type !== null);

        const isCommissionSalaryType = computed(() => form.salary_type === 'Commission');

        const isHourlySalaryType = computed(() => form.salary_type === 'Hourly');

        const rules = computed(() => ({
            note: {
                required: helpers.withMessage('The message field is required.', required),
                maxLength: helpers.withMessage('The note cannot exceed 500 characters.', maxLength(500)),
                $lazy: true,
            },
            job_title: {
                required: helpers.withMessage('The job title field is required.', required),
                maxLength: helpers.withMessage('The job title cannot exceed 100 characters.', maxLength(100)),
                $lazy: true,
            },
            description: {
                required: helpers.withMessage('The description field is required.', required),
                maxLength: helpers.withMessage('The description cannot exceed 10000 characters.', maxLength(10000)),
                $lazy: true,
            },
            hours_per_month: {
                required: helpers.withMessage(
                    'Hours per Month is required.',
                    requiredIf(() => isHourlySalaryType.value),
                ),
                numeric: helpers.withMessage('Hours per Month can only be numbers.', numeric),
                minValue: helpers.withMessage('Amount must be greater than 0', (value) => !isHourlySalaryType.value || value > 0),
                $lazy: true,
            },
            min: {
                required: helpers.withMessage(
                    'Minimum is required.',
                    requiredIf(() => hasSalaryType.value && !isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Minimum can only be numbers.', numeric),
                minValue: helpers.withMessage(
                    'Amount must be greater than 0',
                    (value) => !hasSalaryType.value || isCommissionSalaryType.value || value > 0,
                ),
                $lazy: true,
            },
            max: {
                required: helpers.withMessage(
                    'Maximum is required.',
                    requiredIf(() => hasSalaryType.value && !isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Maximum can only be numbers.', numeric),
                minValue: helpers.withMessage(
                    'Amount must be greater than 0',
                    (value) => !hasSalaryType.value || isCommissionSalaryType.value || value > 0,
                ),
                $lazy: true,
            },
            basic_min: {
                required: helpers.withMessage(
                    'Minimum is required.',
                    requiredIf(() => isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Minimum can only be numbers.', numeric),
                minValue: helpers.withMessage('Amount must be greater than 0', (value) => !isCommissionSalaryType.value || value > 0),
                $lazy: true,
            },
            basic_max: {
                required: helpers.withMessage(
                    'Maximum is required.',
                    requiredIf(() => isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Maximum can only be numbers.', numeric),
                minValue: helpers.withMessage('Amount must be greater than 0', (value) => !isCommissionSalaryType.value || value > 0),
                $lazy: true,
            },
            comm_min: {
                required: helpers.withMessage(
                    'Commission Minimum is required.',
                    requiredIf(() => isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Commission Minimum can only be numbers.', numeric),
                minValue: helpers.withMessage('Amount must be greater than 0', (value) => !isCommissionSalaryType.value || value > 0),
                $lazy: true,
            },
            comm_max: {
                required: helpers.withMessage(
                    'Commission Maximum is required.',
                    requiredIf(() => isCommissionSalaryType.value),
                ),
                numeric: helpers.withMessage('Commission Maximum can only be numbers.', numeric),
                minValue: helpers.withMessage('Amount must be greater than 0', (value) => !isCommissionSalaryType.value || value > 0),
                $lazy: true,
            },
        }));

        const v$ = useVuelidate(rules, form);

        return {
            form,
            error,
            hasSalaryType,
            isCommissionSalaryType,
            isHourlySalaryType,
            initialFormData,
            loading,
            v$,
            salaryTypes,
        };
    },

    computed: {
        message() {
            let message = `<b>New Job Invitation:</b><br/><br/>`;
            message += `${this.form.note}<br/><br/>`;
            message += `<b>Job Title:</b><br/>${this.form.job_title}<br/><br/>`;
            message += `<b>Job Description:</b><br/>${this.form.description}`;

            const data = new FormData();

            if (this.hasSalaryType) {
                message += '<br/><br/>';
                message += '<b>Salary:</b><br/>';
                message += `Type: ${this.form.salary_type}<br/>`;
                message += this.isHourlySalaryType ? 'Basic per Hour: ' : 'Basic per Month: ';
                message += `R${this.form.min || this.form.basic_min} - R${this.form.max || this.form.basic_max}<br/>`;
                message += this.isHourlySalaryType ? 'Hours per Month: ' : '';
                message += this.isHourlySalaryType ? `${this.form.hours_per_month}<br/>` : '';
                message += this.isCommissionSalaryType ? 'Commission per Month: ' : '';
                message += this.isCommissionSalaryType ? `R${this.form.comm_min} - R${this.form.comm_min}` : '';
            }

            data.append('body', message);

            if (this.form.document) {
                data.append('document', this.form.document);
            }

            return data;
        },

        messagePlaceholder() {
            return `Hi ${this.applicant_name}, I enjoyed reading your profile and would love to chat to you about what we have to offer.`;
        },
    },

    mounted() {
        this.form.note = this.messagePlaceholder;
    },

    methods: {
        closeDialog() {
            this.$emit('close-dialog');
        },

        async initiateConversation() {
            await this.v$.$validate();

            if (this.v$.$invalid) return;

            this.loading = true;

            this.$store
                .dispatch('conversations/createConversation', {
                    candidate_uuid: this.applicant_uuid,
                    initiated_from_job_invite: 1,
                })
                .then((response) => {
                    this.sendMessage(response.uuid);
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                    this.loading = false;
                });
        },

        resetForm() {
            this.v$.$reset();
            this.form = this.initialFormData();
            this.error = null;
            this.loading = false;
        },

        async sendMessage(conversation_uuid) {
            await this.$store
                .dispatch('conversations/sendMessage', {
                    conversation_uuid: conversation_uuid,
                    data: this.message,
                })
                .then((response) => {
                    this.resetForm();

                    this.closeDialog();

                    this.$toast.show({
                        type: 'success',
                        message: 'Job invitation sent.',
                    });
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                    this.loading = false;
                });
        },

        showListedJobForm() {
            this.resetForm();

            this.$emit('show-listed-job-form');
        },
    },
};
